import { useCallback, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';

import { avatarColorIndex, nameInitials } from '@/components/ui/avatar';
import { PersonReworkModal } from '@/features/document-rework/components/PersonReworkModal';
import { cn } from '@/components/ui/cn';
import { plural } from '@/lib/plural';
import { ExportMenu } from '@/components/ui/ExportMenu';
import { CardFrame, CardNote, QueryBody, RANGE_NOTE } from '@/components/ui/card-frame';

import { analysisQuery, rangeProblem } from '../presentation';
import type { ReworkSummary, TimeStudyFilters } from '../types';
import { useReworkSummary } from '../use-time-study';
import { endOfBusinessDayIso, startOfBusinessDayIso } from '@/lib/time';

// Reprocesos atribuidos (so=420, spec §4.2 tarjeta 4; so=380): por persona originadora,
// con el desglose por rol y los documentos, más la fila "Sin atribuir".

type Person = ReworkSummary['people'][number];

// Persona con el diálogo abierto, en la URL (UX-NAV-02): volver de la ficha lo reabre.
const PERSON_PARAM = 'reprocesos';
const PERSON_KEY = /^(none|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

const personKey = (p: Person): string => p.userId ?? 'none';

function personName(p: Person): string {
  return p.name ?? (p.userId === null ? 'Sin atribuir' : 'Usuario sin nombre');
}

function usePersonParam(): [string | null, (key: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(PERSON_PARAM);
  const set = useCallback(
    (key: string | null) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (key === null) {
            p.delete(PERSON_PARAM);
          } else {
            p.set(PERSON_PARAM, key);
          }
          return p;
        },
        { replace: true },
      ),
    [setParams],
  );
  return [raw !== null && PERSON_KEY.test(raw) ? raw : null, set];
}

function breakdown(p: Person): string {
  const roles = [
    p.byRole.elaborador > 0 ? `Elaboración ${p.byRole.elaborador}` : null,
    p.byRole.revisor > 0 ? `Revisión ${p.byRole.revisor}` : null,
  ].filter((x) => x !== null);
  return [...roles, plural(p.documentCount, 'documento', 'documentos')].join(' · ');
}

export function ReworkCard({
  filters,
  companyId,
}: {
  filters: TimeStudyFilters;
  companyId: string;
}): ReactElement {
  const [openKey, setOpenKey] = usePersonParam();
  const problem = rangeProblem(filters);
  const query = useReworkSummary(filters, { companyId, enabled: problem === null });
  // Solo se abre una persona que está en el resumen vigente (mismo rango y filtros).
  const person =
    problem === null && openKey !== null
      ? query.data?.people.find((p) => personKey(p) === openKey)
      : undefined;
  return (
    <CardFrame
      title="Reprocesos atribuidos"
      actions={
        problem === null && (
          <ExportMenu
            compact
            path="/api/v1/quality/rework/summary/export"
            query={analysisQuery(filters)}
            title="Reprocesos atribuidos"
          />
        )
      }
      count={
        query.data ? plural(query.data.totals.reworkCount, 'reproceso', 'reprocesos') : undefined
      }
    >
      {problem !== null ? (
        <CardNote>{RANGE_NOTE}</CardNote>
      ) : (
        <QueryBody
          query={query}
          isEmpty={(d) => d.people.length === 0}
          emptyText="No hubo reprocesos en el rango."
        >
          {(d) => (
            <>
              <ul className="rlist">
                {d.people.map((p) => {
                  const name = personName(p);
                  return (
                    <li key={personKey(p)} className="rowb">
                      <span
                        className={cn(
                          'av sm',
                          p.userId !== null && `p${avatarColorIndex(p.userId)}`,
                        )}
                        aria-hidden="true"
                      >
                        {p.userId === null ? '—' : nameInitials(name)}
                      </span>
                      <div className="grow">
                        {/* Detalle por persona (spec 2026-10-14 §5 D2): sus devoluciones. */}
                        <button
                          type="button"
                          className="rowpick nm rw-person"
                          aria-label={
                            p.userId === null
                              ? 'Ver los reprocesos sin atribuir'
                              : `Ver los reprocesos de ${name}`
                          }
                          onClick={() => setOpenKey(personKey(p))}
                        >
                          {name}
                        </button>
                        <div className="rl">{breakdown(p)}</div>
                      </div>
                      <span className={p.reworkCount > 1 ? 'badge b-red' : 'badge b-warn'}>
                        {plural(p.reworkCount, 'reproceso', 'reprocesos')}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="cardfoot">
                Un reproceso es una devolución a una etapa previa; el tiempo se atribuye a quien
                originó la devolución.
              </p>
            </>
          )}
        </QueryBody>
      )}
      {person !== undefined && (
        <PersonReworkModal
          person={{ userId: person.userId, name: personName(person) }}
          filter={{
            from: startOfBusinessDayIso(filters.desde),
            to: endOfBusinessDayIso(filters.hasta),
            documentTypeId: filters.tipo,
            processAreaId: filters.area,
          }}
          companyId={companyId}
          onClose={() => setOpenKey(null)}
        />
      )}
    </CardFrame>
  );
}
